import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, Card, Alert } from '@parsina/framework';
import { Sparkles } from 'lucide-react';

import { C, CT, GRADIENT_BRAND_DARK } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useMe } from '../../hooks/useMe.js';
import { useWallet } from '../../hooks/useWallet.js';
import { fmtParseh } from '../../constants/pricing';
import { useCreditPrices } from '../../lib/useCreditPrices.js';
import { DEFAULT_PAGE_SIZE, clampPage } from '../../lib/paging.js';
import { CostChip } from '../../components/common/ParsehCost.jsx';
import TopUpCard from './TopUpCard.jsx';
import GiftCodeCard from './GiftCodeCard.jsx';
import TransactionList from './TransactionList.jsx';

// پارسه — the desktop wallet screen.
//
// ┌──────────────────────────────────┬────────────────────────┐
// │ balance hero                      │ price list             │
// │ transactions                      │ top-up                 │
// │                                   │ gift code              │
// └──────────────────────────────────┴────────────────────────┘
//
// Modelled on doctor-app/parsina.client.web/src/screens/Credits, reduced to what
// a student panel spends on. The doctor screen's per-visit KPI strip, date-range
// filter and per-service breakdown are deliberately absent: this app has no
// visits, and «میانگین هر ویزیت» on a student's screen is a number about somebody
// else's job.
//
// ── The free grant is stated, and there is no button for it ─────────────
//
// The wallet auto-creates on its FIRST balance read with 500 پارسه and refills to
// 500 at each calendar-month rollover. So reading `myWalletBalance` IS the
// starter grant, and it renews itself. There is deliberately no
// «دریافت اعتبار رایگان» control anywhere in this app: a grant mutation plus a
// "has been granted" flag would be a second source of truth that can disagree
// with the wallet, and would double-grant on any retry of a call whose response
// was lost (CORE.md §8).

export default function CreditsWeb() {
  const { t, formatNumber } = useTranslation();
  // The policy's figures once the list has arrived; the shipped constants until then.
  const { priceList } = useCreditPrices();
  const { balance, loading } = useMe();
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = searchParams.get('type') || null;
  const urlPage = Number(searchParams.get('page')) || 1;

  const { ledger, gifts, topUp, refreshBalance } = useWallet({
    page: urlPage,
    pageSize: DEFAULT_PAGE_SIZE,
    type: filter,
  });

  // Returning from the bank: the wallet's own HTTP callback credited the wallet
  // and nothing in this tab was told about it. One re-read on mount is what turns
  // «۳۲۰» back into the number the student just paid for.
  React.useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  const page = clampPage(urlPage, ledger.totalCount, DEFAULT_PAGE_SIZE);
  React.useEffect(() => {
    if (ledger.loading || ledger.error || page === urlPage) return;
    const params = new URLSearchParams(searchParams);
    if (page <= 1) params.delete('page');
    else params.set('page', String(page));
    setSearchParams(params, { replace: true });
  }, [page, urlPage, ledger.loading, ledger.error, searchParams, setSearchParams]);

  const setFilter = (value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set('type', value);
    else params.delete('type');
    params.delete('page'); // a new filter starts at page one
    setSearchParams(params, { replace: true });
  };

  const setPage = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete('page');
    else params.set('page', String(next));
    setSearchParams(params);
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}>
      <PageHeader title={t('credits.title')} sub={t('credits.sub')} />

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 32px 40px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 320px)', gap: 20 }}>
          {/* ── The ledger column ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
            <div
              style={{
                background: GRADIENT_BRAND_DARK,
                borderRadius: 20,
                padding: '22px 24px',
                color: '#fff',
                position: 'relative',
                overflow: 'hidden',
              }}
              data-testid="balance-hero"
            >
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: -40,
                  insetInlineStart: -40,
                  width: 150,
                  height: 150,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.05)',
                }}
              />
              <div style={{ position: 'relative' }}>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', fontWeight: 700 }}>
                  {t('credits.balance.label')}
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 6 }}>
                  <span style={{ fontSize: 34, fontWeight: 800, lineHeight: 1 }} dir="ltr">
                    {/* «—» while the read is in flight, never «۰»: a zero here
                        tells a student with plenty that they have nothing. */}
                    {loading && balance == null
                      ? t('common.labels.none')
                      : fmtParseh(balance, formatNumber)}
                  </span>
                  <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', marginBottom: 4 }}>
                    {t('common.units.parseh')}
                  </span>
                </div>
                {loading && balance == null && (
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.55)', marginTop: 8 }}>
                    {t('credits.balance.loading')}
                  </div>
                )}
              </div>
            </div>

            <TransactionList
              ledger={ledger}
              filter={filter}
              onFilter={setFilter}
              page={page}
              onPage={setPage}
            />
          </div>

          {/* ── The rail ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
            <Alert
              variant="success"
              title={t('credits.free.title')}
              icon={<Sparkles size={18} aria-hidden />}
            >
              {t('credits.free.body')}
            </Alert>

            <Card>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: C.textPrimary, marginBottom: 10 }}>
                {t('credits.priceList.title')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {priceList.map((row, index) => (
                  <div
                    key={row.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '7px 0',
                      borderBottom: index < priceList.length - 1 ? `1px solid ${C.border}` : 'none',
                    }}
                  >
                    <span style={{ flex: 1, fontSize: 12.5, color: CT.ink2 }}>{t(row.labelKey)}</span>
                    {/* The SAME chip the assistant shows beside the button that
                        spends it, reading the SAME constant — so the advertised
                        price and the shown price cannot disagree. */}
                    <CostChip cost={row.cost} />
                  </div>
                ))}
              </div>
            </Card>

            <TopUpCard topUp={topUp} />

            <GiftCodeCard gifts={gifts} onRedeemed={() => ledger.reload()} />
          </div>
        </div>
      </div>
    </div>
  );
}
